import { useCallback, useEffect, useRef, useState } from "react";

const LIMIT = 100, SETTLE = 400;
const same = (a, b) => a && b && Object.keys(a).every((k) => a[k] === b[k]);

export function useHistory(state, apply) {
  const h = useRef({ past: [], future: [], last: null, timer: null, skip: false });
  const [counts, setCounts] = useState({ undo: 0, redo: 0 });
  const sync = () => setCounts({ undo: h.current.past.length, redo: h.current.future.length });

  const commit = useCallback((cur) => {
    const r = h.current;
    clearTimeout(r.timer);
    if (!r.last || same(r.last, cur)) return;
    r.past = [...r.past, r.last].slice(-LIMIT);
    r.future = [];
    r.last = cur;
    sync();
  }, []);

  const keys = Object.keys(state), deps = keys.map((k) => state[k]);
  useEffect(() => {
    const r = h.current;
    if (r.skip || !r.last) { r.skip = false; if (r.last) r.last = state; return; }
    clearTimeout(r.timer);
    r.timer = setTimeout(() => commit(state), SETTLE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const stateRef = useRef(state);
  stateRef.current = state;
  const step = (from, to) => {
    commit(stateRef.current);
    const r = h.current;
    if (!r[from].length) return;
    const snap = r[from][r[from].length - 1];
    r[from] = r[from].slice(0, -1);
    r[to] = [...r[to], r.last];
    r.last = snap;
    r.skip = true;
    apply(snap);
    sync();
  };

  return {
    reset: (snap) => { h.current = { past: [], future: [], last: snap, timer: null, skip: true }; sync(); },
    undo: () => step("past", "future"),
    redo: () => step("future", "past"),
    canUndo: counts.undo > 0 || (h.current.last && !same(h.current.last, state)),
    canRedo: counts.redo > 0,
  };
}
